import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { LogoDetails } from "../components/logo-details.component";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { useNavigate } from "react-router";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { performCavemanReFetch } from "../../../../utils/functions/caveman-utils";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { useQueryClient } from "@tanstack/react-query";
import { withToasts } from "../../../components/Toast/Toast";
import { useTranslation } from "react-i18next";
import { useHealPendingHighRes } from "../../../../hooks/useHealPendingHighRes";

export function LogoDetailsWrapper({
  logo,
  onViewRejectNote,
  closeDialog,
}: {
  logo: BaseLogo;
  onViewRejectNote: (logo: BaseLogo) => void;
  closeDialog: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoRepository } = useRepositoriesContext();
  const { profile } = useProfileContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data: logoDetails, isError } = logoRepository.useLogo(
    getLogoOrgId(logo),
    logo.id
  );

  const {
    handleGoToEditLogo,
    handleDeleteLogo,
    handleChangeTitle,
    handlePlaceVerdict,
    handleAddToCart,
    handleCopyThenGoToLogo,
    handleSkipApproval,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    isLoading: isActionRunning,
    handleRejectAndDelete,
  } = useLogoActionHandler();

  // Render optimistically with the logo the list already loaded (same entity,
  // same mapper - mapToLogo - as the detail query, incl. its browser-cached
  // low-res image) so the modal paints INSTANTLY, and swap to the full GetLogo
  // once the background fetch resolves. Previously the whole modal was gated
  // behind a spinner while GetLogo ran (~1.4-3.7s server-side). No extra
  // network: the list already fetched this logo. Only two things the popup
  // shows actually require the full fetch: designCount (list rows always carry
  // 0) and the fresh high-res image render - both handled via detailStatus
  // below, never presented with a wrong value meanwhile.
  const shownLogo = logoDetails ?? logo;
  const detailStatus = logoDetails ? "ready" : isError ? "error" : "loading";

  // Fresh ImageLogo (upload/clone): the cached GetLogo can carry a blank or
  // identical hi-res because the logo system isn't done with the serial yet,
  // and nothing ever refetches it (refetchOnMount is off globally). Bounded
  // heal: up to three background refetches (5s/15s/45s), stopping as soon as
  // a genuine high-res lands or the popup closes. Judged on logoDetails (the
  // real detail response), never the list stub, so settled logos never poll.
  useHealPendingHighRes(logo.id, logoDetails);

  function handleViewDesign(logo: BaseLogo) {
    navigate(
      `/design?pageSize=25&pageIndex=1&filterBy=designlogos.logoid&filterOn=${logo.id}`
    );
  }

  function handleCreateDesign(logo: BaseLogo) {
    const params = new URLSearchParams({
      addLogoId: String(logo.id),
      logoTitle: logo.title,
    });
    navigate(`/design/create?${params.toString()}`);
  }

  function handleTitle(title: string) {
    handleChangeTitle(getLogoOrgId(logo), logo.id, title);
  }

  async function onDelete(logo: BaseLogo) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    try {
      await handleDeleteLogo(logo);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete logo:", error);
    }
  }

  function onSendApproval(logo: BaseLogo, email: string) {
    // Use conditional approval for Customers to preserve existing approval statuses
    // Use regular approval for Vendors who have full control
    if (profile?.userOrganizationInformations.role === Role.EndUser) {
      handleSendApprovalWithEndCustomerPending(logo, email).then(
        performCavemanReFetch
      );
    } else {
      handleSendApproval(logo, email);
    }
  }

  function onRejectAndDelete(logo: BaseLogo, note?: string | undefined) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handleRejectAndDelete(logo, note);
  }

  function onPlaceVerdict(logo: BaseLogo, verdict: Verdict) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handlePlaceVerdict(logo, verdict);
  }

  async function handleAssignCustomer(customerId: number) {
    const transaction = async () => {
      await apiClient.assignCustomer(getLogoOrgId(logo), logo.id, customerId);
      await queryClient.invalidateQueries(["logos"]);
      await queryClient.invalidateQueries(["logos", logo.id]);
    };

    await withToasts(
      transaction,
      t("logoList.assigningCustomer"),
      t("logoList.customerAssigned"),
      t("logoList.customerAssignFailed")
    );
  }

  return (
    <LogoDetails
      // Remount when the full GetLogo arrives so useState initializers
      // (order quantity, editable title) re-run against the complete data.
      // Both images are browser-cached by then, so the remount repaints
      // instantly (same guard as design-details.wrapper).
      key={logoDetails ? "summary" : "list-stub"}
      onPlaceVerdict={onPlaceVerdict}
      logo={shownLogo}
      detailStatus={detailStatus}
      onAddToCart={handleAddToCart}
      onShowRejectionNote={onViewRejectNote}
      onDeleteLogo={onDelete}
      onTitleChange={handleTitle}
      onViewDesigns={handleViewDesign}
      onEditLogo={handleGoToEditLogo}
      onCopyLogo={handleCopyThenGoToLogo}
      onSkipApproval={handleSkipApproval}
      onSendApproval={onSendApproval}
      // Only user actions overlay the modal - the background detail fetch
      // never gates the render. If it fails we keep the list data and show a
      // discreet per-field notice inside LogoDetails (detailStatus="error")
      // instead of replacing the whole popup with an error view.
      isLoading={isActionRunning}
      onRejectAndDelete={onRejectAndDelete}
      onAssignCustomer={handleAssignCustomer}
      onCreateDesign={handleCreateDesign}
    />
  );
}
